import React, { useEffect, useMemo, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

/**
 * Guided in-app tour: dims the page and spotlights the current-step target.
 * Each step shape:
 *   { title, body (HTML string), tab (optional testid suffix), target (testid string, optional), cta (optional label) }
 * If `tab` is set, we click the `tab-<tab>` button before displaying the step (dashboards only).
 * If `target` is set, we compute the rect and cut a hole in the dim overlay; else we center the card.
 */
const PADDING = 8;

export default function GuidedTour({ open, steps, onClose, brand = "Carinthia VII" }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[idx];

  const positionSpotlight = useCallback(() => {
    if (!step?.target) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - PADDING, left: r.left - PADDING, w: r.width + PADDING * 2, h: r.height + PADDING * 2 });
    try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch {}
  }, [step]);

  // When step changes: optionally click a tab, wait for render, then measure the target.
  useEffect(() => {
    if (!open || !step) return;
    let cancelled = false;
    (async () => {
      if (step.tab) {
        const tabBtn = document.querySelector(`[data-testid="tab-${step.tab}"]`);
        if (tabBtn) tabBtn.click();
      }
      await new Promise((r) => setTimeout(r, step.tab ? 350 : 60));
      if (!cancelled) positionSpotlight();
    })();
    // Track resize/scroll so the ring stays put.
    const onResize = () => positionSpotlight();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => { cancelled = true; window.removeEventListener("resize", onResize); window.removeEventListener("scroll", onResize, true); };
  }, [open, idx, positionSpotlight, step]);

  useEffect(() => { if (open) setIdx(0); }, [open]);

  if (!open || !step) return null;

  const isLast = idx === steps.length - 1;
  const isFirst = idx === 0;

  // Tooltip position: below the target if there's room, otherwise above; if no target, center.
  let tipStyle = {};
  const TIP_W = 380, TIP_H_EST = 220;
  if (rect) {
    const vh = window.innerHeight, vw = window.innerWidth;
    const below = rect.top + rect.h + 16;
    const preferAbove = below + TIP_H_EST > vh - 20;
    const top = preferAbove ? Math.max(20, rect.top - TIP_H_EST - 16) : below;
    let left = rect.left + rect.w / 2 - TIP_W / 2;
    left = Math.max(20, Math.min(left, vw - TIP_W - 20));
    tipStyle = { position: "fixed", top, left, width: TIP_W, zIndex: 100001 };
  } else {
    tipStyle = { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: TIP_W, zIndex: 100001 };
  }

  // Dim overlay: 4 rects around the spotlight (or single fullscreen when no target).
  const dim = "rgba(15, 23, 42, 0.72)";
  const dimStyleBase = { position: "fixed", background: dim, zIndex: 100000, pointerEvents: "auto" };

  return (
    <>
      {rect ? (
        <>
          <div style={{ ...dimStyleBase, top: 0, left: 0, right: 0, height: rect.top }} />
          <div style={{ ...dimStyleBase, top: rect.top, left: 0, width: rect.left, height: rect.h }} />
          <div style={{ ...dimStyleBase, top: rect.top, left: rect.left + rect.w, right: 0, height: rect.h }} />
          <div style={{ ...dimStyleBase, top: rect.top + rect.h, left: 0, right: 0, bottom: 0 }} />
          {/* Highlight ring */}
          <div
            style={{
              position: "fixed",
              top: rect.top,
              left: rect.left,
              width: rect.w,
              height: rect.h,
              border: "2px solid rgb(198, 168, 106)",
              boxShadow: "0 0 0 4px rgba(198,168,106,0.35), 0 0 30px rgba(198,168,106,0.35)",
              borderRadius: 4,
              pointerEvents: "none",
              zIndex: 100000,
              transition: "all .28s ease",
            }}
            data-testid="tour-spotlight"
          />
        </>
      ) : (
        <div style={{ ...dimStyleBase, inset: 0 }} />
      )}

      {/* Tooltip card */}
      <div style={tipStyle} className="bg-background border border-accent/60 shadow-2xl" data-testid="tour-card">
        <div className="px-5 pt-5 pb-3 border-b border-border flex items-start justify-between gap-3">
          <div>
            <div className="overline text-accent flex items-center gap-1 text-[10px]">
              <Sparkles className="h-3 w-3" /> {brand} · Guided training
            </div>
            <div className="font-display text-xl mt-1 leading-tight" data-testid="tour-title">{step.title}</div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-primary"
            data-testid="tour-close"
            aria-label="Close tour"
          ><X className="h-4 w-4" /></button>
        </div>
        <div
          className="px-5 py-4 text-sm text-foreground/85 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: step.body }}
          data-testid="tour-body"
        />
        <div className="px-5 pb-5 flex items-center justify-between">
          <div className="flex gap-1" data-testid="tour-progress">
            {steps.map((s, i) => (
              // Use the step's id (or its title as a stable fallback) — tour
              // steps are declared statically so ordering is stable across renders.
              <span key={s.id || s.title || `step-${i}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-accent" : i < idx ? "w-3 bg-accent/40" : "w-3 bg-border"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {!isFirst && (
              <button
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                className="text-xs uppercase tracking-widest px-3 py-2 border border-border hover:border-primary text-muted-foreground hover:text-primary"
                data-testid="tour-prev"
              >
                <ChevronLeft className="h-3 w-3 inline mr-1" />Back
              </button>
            )}
            <button
              onClick={() => (isLast ? onClose() : setIdx((i) => i + 1))}
              className="text-xs uppercase tracking-widest px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="tour-next"
            >
              {isLast ? "Finish" : (step.cta || "Next")}
              {!isLast && <ChevronRight className="h-3 w-3 inline ml-1" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
